/**
 * Item-Level Merge for Multi-Device Sync
 *
 * Instead of replacing entire arrays on each poll, compares items by ID
 * so that edits on different devices to different items are both preserved.
 */

import type { Item } from '@/types';

export interface MergeResult {
  merged: Item[];
  needsWriteBack: boolean;
}

/**
 * Merge local and remote item arrays using dirty/deleted tracking.
 *
 * | Local | Remote | Dirty? | Deleted? | Result                          |
 * |-------|--------|--------|----------|---------------------------------|
 * | -     | yes    | -      | no       | Accept remote (new from other)  |
 * | -     | yes    | -      | yes      | Discard (we deleted it)         |
 * | yes   | -      | yes    | -        | Keep local (we created it)      |
 * | yes   | -      | no     | -        | Drop (remote deleted it)        |
 * | yes   | yes    | no     | -        | Accept remote                   |
 * | yes   | yes    | yes    | -        | Keep local (we edited it)       |
 */
export function mergeItemArrays(
  local: Item[],
  remote: Item[],
  dirtyIds: Set<string>,
  deletedIds: Set<string>
): MergeResult {
  const localMap = new Map<string, Item>();
  for (const item of local) {
    localMap.set(item.id, item);
  }

  const remoteMap = new Map<string, Item>();
  for (const item of remote) {
    remoteMap.set(item.id, item);
  }

  const merged: Item[] = [];
  const seenIds = new Set<string>();
  let needsWriteBack = false;

  // Process all remote items
  for (const [id, remoteItem] of remoteMap) {
    seenIds.add(id);

    if (deletedIds.has(id)) {
      // We deleted this item locally — discard the remote copy
      needsWriteBack = true;
      continue;
    }

    const localItem = localMap.get(id);

    if (!localItem) {
      // New item from another device — accept it
      merged.push(remoteItem);
      continue;
    }

    if (dirtyIds.has(id)) {
      // We edited this item locally — keep our version
      merged.push(localItem);
      needsWriteBack = true;
    } else {
      // We didn't touch it — accept remote version
      merged.push(remoteItem);
    }
  }

  // Process local-only items (not in remote)
  for (const [id, localItem] of localMap) {
    if (seenIds.has(id)) continue;
    seenIds.add(id);

    if (dirtyIds.has(id)) {
      // We created this item locally — keep it
      merged.push(localItem);
      needsWriteBack = true;
    }
    // else: not dirty and not in remote = remote deleted it, drop it
  }

  // Also flag writeBack if we kept any deleted items out that remote still has
  // (already handled above in the deletedIds check)

  return { merged, needsWriteBack };
}
